import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DocumentArrowDownIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import {
  Button,
  Heading,
  Input,
  LoadingSpinner,
  Select,
  SortableTableHeader,
  Text,
} from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { marketingDespesasService } from '../../services/marketingDespesas'
import type { MarketingExpense, MarketingExpenseInput } from '../../types/marketingDespesas'
import {
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  PAGE_SIZE,
  PERM_LOADING,
  paginateRows,
  sortRows,
} from '../Estoque/estoqueUi'
import EstoquePagination from '../Estoque/EstoquePagination'
import { formatCurrency, formatDateOnlyPtBr } from './notasUi'
import DespesasFormModal from './DespesasFormModal'

const MODULE_NAME = 'marketing_despesas'

const MarketingDespesas: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete, logView } = useUserLogs()
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [rows, setRows] = useState<MarketingExpense[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [paymentSourceFilter, setPaymentSourceFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sortKey, setSortKey] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<MarketingExpense | null>(null)
  const [saving, setSaving] = useState(false)
  const [viewRow, setViewRow] = useState<MarketingExpense | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await marketingDespesasService.list()
      setRows(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar despesas.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const supplierOptions = useMemo(
    () =>
      rows
        .filter((row) => row.supplier?.id && row.supplier?.name)
        .map((row) => ({ id: row.supplier!.id, name: row.supplier!.name }))
        .filter((row, index, arr) => arr.findIndex((item) => item.id === row.id) === index)
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [rows]
  )

  const paymentSourceOptions = useMemo(
    () =>
      rows
        .filter((row) => row.payment_source?.id && row.payment_source?.name)
        .map((row) => ({ id: row.payment_source!.id, name: row.payment_source!.name }))
        .filter((row, index, arr) => arr.findIndex((item) => item.id === row.id) === index)
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [rows]
  )

  const hasActiveFilters = Boolean(
    search.trim() || supplierFilter || paymentSourceFilter || dateFrom || dateTo
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((row) => {
      if (supplierFilter && row.supplier?.id !== supplierFilter) return false
      if (paymentSourceFilter && row.payment_source?.id !== paymentSourceFilter) return false

      const createdAt = row.created_at?.slice(0, 10) || ''
      if (dateFrom && (!createdAt || createdAt < dateFrom)) return false
      if (dateTo && (!createdAt || createdAt > dateTo)) return false

      if (!q) return true
      return (row.action_name || '').toLowerCase().includes(q)
    })
  }, [rows, search, supplierFilter, paymentSourceFilter, dateFrom, dateTo])

  const sorted = useMemo(() => {
    return sortRows(filtered, sortKey, sortOrder, (row, key) => {
      if (key === 'amount') return Number(row.amount)
      if (key === 'created_at') return row.created_at || ''
      if (key === 'action_name') return row.action_name || ''
      if (key === 'supplier') return row.supplier?.name || ''
      if (key === 'payment_source') return row.payment_source?.name || ''
      return ''
    })
  }, [filtered, sortKey, sortOrder])

  const paginated = useMemo(() => paginateRows(sorted, page, PAGE_SIZE), [sorted, page])

  const openCreate = () => {
    setEditing(null)
    setModalOpen(true)
  }

  const openEdit = (row: MarketingExpense) => {
    setEditing(row)
    setModalOpen(true)
  }

  const handleSubmit = async (input: MarketingExpenseInput, receiptFile: File | null) => {
    setSaving(true)
    try {
      if (editing) {
        const updated = await marketingDespesasService.update(editing.id, input)
        if (receiptFile) {
          const current = updated.attachments || []
          for (const att of current) {
            await marketingDespesasService.softDeleteAttachment(att.id, {
              deleted_by: user?.id || null,
              deleted_reason: 'replaced receipt on update',
            })
          }
          await marketingDespesasService.uploadAttachment(receiptFile, updated.id, user?.id || null)
        }
        await logUpdate(
          'marketing_expense',
          updated.id,
          `Atualizou despesa: ${updated.action_name}`,
          {
            action_name: editing.action_name,
            supplier_id: editing.supplier_id,
            payment_source_id: editing.payment_source_id,
            amount: editing.amount,
          },
          {
            action_name: updated.action_name,
            supplier_id: updated.supplier_id,
            payment_source_id: updated.payment_source_id,
            amount: updated.amount,
          }
        )
      } else {
        const created = await marketingDespesasService.create(input, user?.id || null)
        if (receiptFile) {
          await marketingDespesasService.uploadAttachment(receiptFile, created.id, user?.id || null)
        }
        await logCreate(
          'marketing_expense',
          created.id,
          `Criou despesa: ${created.action_name}`,
          { action_name: created.action_name, amount: created.amount }
        )
      }
      setModalOpen(false)
      setEditing(null)
      await load()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: MarketingExpense) => {
    const ok = window.confirm(`Excluir a despesa "${row.action_name}"?`)
    if (!ok) return
    try {
      await marketingDespesasService.softDelete(row.id, {
        deleted_by: user?.id || null,
        deleted_reason: 'Exclusão pela tela de despesas',
      })
      await logDelete(
        'marketing_expense',
        row.id,
        `Excluiu despesa: ${row.action_name}`,
        { action_name: row.action_name }
      )
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir despesa.')
    }
  }

  const handleView = async (row: MarketingExpense) => {
    setViewRow(row)
    await logView('marketing_expense', row.id, `Visualizou despesa: ${row.action_name}`)
  }

  const openAttachment = async (row: MarketingExpense) => {
    const att = row.attachments?.[0]
    if (!att) return
    const url = await marketingDespesasService.getAttachmentUrl(att.file_path)
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortOrder(key === 'created_at' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  const handleSort = (key: string) => toggleSort(key)

  const clearFilters = () => {
    setSearch('')
    setSupplierFilter('')
    setPaymentSourceFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  return (
    <div className="h-full flex flex-col overflow-x-hidden overflow-y-auto">
      <PermissionGuard moduleName={MODULE_NAME} permissionType="show">
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                Despesas
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                Cadastre despesas de ações/eventos e gere parcelas no financeiro.
              </Text>
            </div>
            <PermissionGuard
              moduleName={MODULE_NAME}
              permissionType="insert"
              loadingComponent={PERM_LOADING}
              fallback={null}
            >
              <Button variant="solid" onClick={openCreate} className="inline-flex items-center gap-2">
                <PlusIcon className="h-4 w-4" />
                Nova despesa
              </Button>
            </PermissionGuard>
          </div>

          <div className="mb-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Buscar
              </label>
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
                placeholder="Buscar por ação"
              />
            </div>
            <div className="min-w-[200px]">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Fornecedor
              </label>
              <Select
                value={supplierFilter}
                onChange={(e) => {
                  setSupplierFilter(e.target.value)
                  setPage(1)
                }}
              >
                <option value="">Todos</option>
                {supplierOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="min-w-[200px]">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Fonte pagadora
              </label>
              <Select
                value={paymentSourceFilter}
                onChange={(e) => {
                  setPaymentSourceFilter(e.target.value)
                  setPage(1)
                }}
              >
                <option value="">Todas</option>
                {paymentSourceOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Data de
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value)
                  setPage(1)
                }}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Data até
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value)
                  setPage(1)
                }}
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="flex items-center gap-1 whitespace-nowrap"
              >
                <XCircleIcon className="h-4 w-4" />
                Limpar filtros
              </Button>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                    <tr>
                      <SortableTableHeader
                        sortKey="action_name"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3 text-left"
                      >
                        Ação/Evento
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="supplier"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3 text-left"
                      >
                        Fornecedor
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="payment_source"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3 text-left"
                      >
                        Fonte pagadora
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="amount"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3 text-left"
                      >
                        Valor
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="created_at"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3 text-left"
                      >
                        Data de inserção
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Recibo
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {paginated.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          {hasActiveFilters
                            ? 'Nenhuma despesa encontrada com os filtros atuais.'
                            : 'Nenhuma despesa cadastrada.'}
                        </td>
                      </tr>
                    ) : (
                      paginated.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {row.action_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {row.supplier?.name || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {row.payment_source?.name || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {formatCurrency(row.amount)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {formatDateOnlyPtBr(row.created_at.slice(0, 10))}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {row.attachments?.length ? (
                              <button
                                type="button"
                                className={ICON_BTN_CLASS}
                                title="Abrir recibo"
                                onClick={() => void openAttachment(row)}
                              >
                                <DocumentArrowDownIcon className="h-4 w-4" />
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                className={ICON_BTN_CLASS}
                                title="Visualizar"
                                onClick={() => void handleView(row)}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              {canUpdate && (
                                <button
                                  type="button"
                                  className={ICON_BTN_CLASS}
                                  title="Editar"
                                  onClick={() => openEdit(row)}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  type="button"
                                  className={ICON_BTN_DANGER_CLASS}
                                  title="Excluir"
                                  onClick={() => void handleDelete(row)}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <EstoquePagination
                page={page}
                pageSize={PAGE_SIZE}
                total={sorted.length}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </PermissionGuard>

      <DespesasFormModal
        open={modalOpen}
        editing={editing}
        saving={saving}
        onClose={() => {
          if (saving) return
          setModalOpen(false)
          setEditing(null)
        }}
        onSubmit={handleSubmit}
      />

      {viewRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setViewRow(null)} />
          <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
            <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                {viewRow.action_name}
              </Heading>
            </div>
            <div className="grid gap-3 px-5 py-5 sm:grid-cols-2">
              <div>
                <Text size="xs" color="muted">
                  Fornecedor
                </Text>
                <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                  {viewRow.supplier?.name || '—'}
                </Text>
              </div>
              <div>
                <Text size="xs" color="muted">
                  Fonte pagadora
                </Text>
                <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                  {viewRow.payment_source?.name || '—'}
                </Text>
              </div>
              <div className="sm:col-span-2">
                <Text size="xs" color="muted">
                  Descrição
                </Text>
                <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                  {viewRow.description || '—'}
                </Text>
              </div>
              <div>
                <Text size="xs" color="muted">
                  Valor
                </Text>
                <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(viewRow.amount)}
                </Text>
              </div>
              <div>
                <Text size="xs" color="muted">
                  Data de inserção
                </Text>
                <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                  {new Date(viewRow.created_at).toLocaleString('pt-BR')}
                </Text>
              </div>
              <div className="sm:col-span-2">
                <Text size="xs" color="muted">
                  Recibo
                </Text>
                <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                  {viewRow.attachments?.[0]?.file_name || '—'}
                </Text>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
              {viewRow.attachments?.length ? (
                <Button variant="outline" onClick={() => void openAttachment(viewRow)}>
                  Abrir recibo
                </Button>
              ) : null}
              <Button variant="solid" onClick={() => setViewRow(null)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketingDespesas
